import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, Spinner,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Label,
} from '@agion/ui'
import { Rocket } from 'lucide-react'
import type { RegistryOverview, TriggerBuildRequest } from './infra_image.service'
import { triggerBuild } from './infra_image.service'
import { listCredentials } from '@/features/secondary/infra_credential.service'
import type { CloudCredential } from '@/features/secondary/infra_credential.service'
import { aptChannelFromVersion } from '@/core/infra_utils'

interface Props {
  open: boolean
  image: RegistryOverview | null
  onClose: () => void
  onSuccess: (operationId: string) => void
}

/**
 * Build confirmation dialog — infers template, version, region, and APT channel
 * from the registry entry. User selects which credential to use for the build.
 */
export function BuildImageDialog({ open, image, onClose, onSuccess }: Props) {
  const { t } = useTranslation(['pages'])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<CloudCredential[]>([])
  const [selectedCredentialId, setSelectedCredentialId] = useState<string>('')
  const [loadingCredentials, setLoadingCredentials] = useState(false)

  // Fetch credentials filtered by cloud provider when dialog opens
  useEffect(() => {
    if (!open || !image) return
    setLoadingCredentials(true)
    setSelectedCredentialId('')
    setError(null)
    listCredentials({ cloudProvider: image.cloud, status: 'active' })
      .then((resp) => {
        const creds = resp.data ?? []
        setCredentials(creds)
        // Auto-select if only one credential exists
        if (creds.length === 1 && creds[0]) {
          setSelectedCredentialId(creds[0].id)
        }
      })
      .catch(() => setCredentials([]))
      .finally(() => setLoadingCredentials(false))
  }, [open, image])

  if (!image) return null

  const aptChannel = aptChannelFromVersion(image.version)

  const handleSubmit = async () => {
    if (!selectedCredentialId) {
      setError(t('pages:images.buildDialog.credentialRequired', 'Select a credential'))
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      const req: TriggerBuildRequest = {
        templateId: `byoh-${image.cloud}`,
        version: image.version,
        credentialId: selectedCredentialId,
        aptChannel,
      }
      const resp = await triggerBuild(req)
      onSuccess(resp.operationId)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger build')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('pages:images.buildDialog.title', 'Build Packer Image')}</DialogTitle>
          <DialogDescription>
            {t('pages:images.buildDialog.confirmDescription', 'Build a VM image from the selected registry version.')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-slate-500">{t('pages:images.buildDialog.cloud')}</span>
            <span className="font-medium">{image.cloud.toUpperCase()}</span>
            <span className="text-slate-500">{t('pages:images.buildDialog.version')}</span>
            <span className="font-mono font-medium">{image.version}</span>
            <span className="text-slate-500">{t('pages:images.buildDialog.aptChannel')}</span>
            <span className="font-medium">{aptChannel}</span>
            <span className="text-slate-500">{t('pages:images.buildDialog.digest')}</span>
            <span className="font-mono text-xs text-slate-400">{image.digest.substring(0, 19)}...</span>
          </div>

          <div className="space-y-2">
            <Label>{t('pages:images.buildDialog.credential', 'Credential')}</Label>
            {loadingCredentials ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Spinner className="h-4 w-4" /> {t('pages:images.buildDialog.loadingCredentials', 'Loading...')}
              </div>
            ) : credentials.length === 0 ? (
              <p className="text-sm text-amber-600">
                {t('pages:images.buildDialog.noCredentials', 'No active credentials for {{cloud}}. Add one in Security → Credentials.', { cloud: image.cloud.toUpperCase() })}
              </p>
            ) : (
              <Select value={selectedCredentialId} onValueChange={setSelectedCredentialId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('pages:images.buildDialog.selectCredential', 'Select credential...')} />
                </SelectTrigger>
                <SelectContent>
                  {credentials.map((cred) => (
                    <SelectItem key={cred.id} value={cred.id}>
                      {cred.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('pages:images.buildDialog.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || !selectedCredentialId}
          >
            {isSubmitting ? (
              <><Spinner className="h-4 w-4 mr-1" /> {t('pages:images.buildDialog.building')}</>
            ) : (
              <><Rocket size={14} className="mr-1" /> {t('pages:images.buildDialog.build')}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
